import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Slider,
  TextField,
  Typography,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import { useAuth } from '../hooks/useAuth';
import { useToastStateSync } from '../hooks/useToastStateSync';
import AppService from '../services/AppService';
import AIService from '../services/AIService';
import FeatureGraphicService from '../services/FeatureGraphicService';
import type { App } from '../types/app';
import type {
  CreateFeatureGraphicDTO,
  FeatureGraphicCropSettings,
  GeneratedFeatureGraphic,
  GeneratedFeatureGraphicAsset,
  GooglePlayFeatureGraphicPublishResponse,
  GooglePlayFeatureGraphicValidationResponse,
} from '../types/featureGraphic';
import type { IconMood, IconStyle } from '../types/icon';

const STYLES: IconStyle[] = ['flat', 'gradient', '3d', 'minimalist', 'illustrative', 'geometric'];
const MOODS: IconMood[] = ['professional', 'playful', 'elegant', 'bold', 'calm'];
const STYLE_MODES = [
  { value: 'manual', label: 'Manual Style' },
  { value: 'ai', label: 'AI Style Mode' },
] as const;
const IMAGE_MODELS = [
  { value: 'gemini-3-pro-image-preview', label: 'Gemini 3 Pro Image Preview' },
  { value: 'gemini-2.5-flash-image', label: 'Gemini 2.5 Flash Image (Nano Banana)' },
  { value: 'gpt-image-2.5-flare', label: 'OpenAI GPT Image 2.5 Flare' },
  { value: 'gpt-image-2.5-sunburst', label: 'OpenAI GPT Image 2.5 Sunburst' },
];

export default function AppFeatureGraphicGenerator() {
  const { user } = useAuth();
  const [apps, setApps] = useState<App[]>([]);
  const [selectedApp, setSelectedApp] = useState<App | null>(null);
  const [generatedGraphics, setGeneratedGraphics] = useState<GeneratedFeatureGraphic[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  useToastStateSync({ success, error });

  const [style, setStyle] = useState<IconStyle>('flat');
  const [mood, setMood] = useState<IconMood>('professional');
  const [styleMode, setStyleMode] = useState<'manual' | 'ai'>('ai');
  const [imageModel, setImageModel] = useState<string>('gpt-image-2.5-flare');
  const [primaryColor, setPrimaryColor] = useState('');
  const [secondaryColor, setSecondaryColor] = useState('');
  const [keywordsInput, setKeywordsInput] = useState('');
  const [keywords, setKeywords] = useState<string[]>([]);
  const [avoidInput, setAvoidInput] = useState('');
  const [avoidElements, setAvoidElements] = useState<string[]>([]);
  const [customPrompt, setCustomPrompt] = useState('');
  const [aiStylePromptDraft, setAiStylePromptDraft] = useState('');
  const [aiSuggestedStyle, setAiSuggestedStyle] = useState<IconStyle>('minimalist');
  const [aiSuggestedMood, setAiSuggestedMood] = useState<IconMood>('professional');
  const [generatingAIStyle, setGeneratingAIStyle] = useState(false);

  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [selectedGraphic, setSelectedGraphic] = useState<GeneratedFeatureGraphic | null>(null);
  const [previewImage, setPreviewImage] = useState<HTMLImageElement | null>(null);
  const [cropSettings, setCropSettings] = useState<FeatureGraphicCropSettings>({
    offsetX: 0,
    offsetY: 0,
    zoom: 1,
  });
  const [finalAsset, setFinalAsset] = useState<GeneratedFeatureGraphicAsset | null>(null);
  const [processing, setProcessing] = useState(false);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [publishTargetGraphic, setPublishTargetGraphic] =
    useState<GeneratedFeatureGraphic | null>(null);
  const [publishConfirmed, setPublishConfirmed] = useState(false);
  const [validatingGooglePlayFeatureGraphic, setValidatingGooglePlayFeatureGraphic] =
    useState(false);
  const [publishingGooglePlayFeatureGraphic, setPublishingGooglePlayFeatureGraphic] =
    useState(false);
  const [featureGraphicValidationResult, setFeatureGraphicValidationResult] =
    useState<GooglePlayFeatureGraphicValidationResponse | null>(null);
  const [featureGraphicPublishResult, setFeatureGraphicPublishResult] =
    useState<GooglePlayFeatureGraphicPublishResponse | null>(null);
  const [publishProgress, setPublishProgress] = useState(0);
  const [publishProgressLabel, setPublishProgressLabel] = useState('');
  const [publishView, setPublishView] = useState<'form' | 'progress' | 'success'>('form');
  const [expandedPublishValidationSections, setExpandedPublishValidationSections] = useState({
    comparison: true,
    issues: true,
  });
  const [expandedFeatureGraphicPreview, setExpandedFeatureGraphicPreview] = useState<{
    open: boolean;
    title: string;
    url: string;
  }>({
    open: false,
    title: '',
    url: '',
  });

  const loadApps = useCallback(async () => {
    if (!user?.email) return;
    try {
      const userApps = await AppService.getUserApps(user.email);
      setApps(userApps);
    } catch (loadError) {
      console.error(loadError);
      setError('Failed to load registered apps.');
    }
  }, [user?.email]);

  const loadGraphics = useCallback(async () => {
    if (!selectedApp || !user?.email) return;
    try {
      const records = await FeatureGraphicService.getFeatureGraphicsByAppId(selectedApp.appId, user.email);
      setGeneratedGraphics(records);
    } catch (loadError) {
      console.error(loadError);
      setError('Failed to load feature graphics.');
    }
  }, [selectedApp, user?.email]);

  useEffect(() => {
    loadApps();
  }, [loadApps]);

  useEffect(() => {
    loadGraphics();
  }, [loadGraphics]);

  const cropPreviewCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const cropPreviewMetricsRef = useRef<{ maxShiftX: number; maxShiftY: number } | null>(null);
  const cropDragStateRef = useRef<{
    pointerId: number | null;
    startX: number;
    startY: number;
    startOffsetX: number;
    startOffsetY: number;
    active: boolean;
  }>({
    pointerId: null,
    startX: 0,
    startY: 0,
    startOffsetX: 0,
    startOffsetY: 0,
    active: false,
  });
  const [isDraggingCropPreview, setIsDraggingCropPreview] = useState(false);

  const parseJson = <T,>(text: string): T | null => {
    const raw = String(text || '').trim();
    if (!raw) return null;
    try {
      return JSON.parse(raw) as T;
    } catch {
      // continue
    }
    const fenced =
      raw.match(/```json\s*([\s\S]*?)\s*```/i)?.[1] ||
      raw.match(/```\s*([\s\S]*?)\s*```/i)?.[1];
    if (fenced) {
      try {
        return JSON.parse(fenced) as T;
      } catch {
        // continue
      }
    }
    const start = raw.indexOf('{');
    const end = raw.lastIndexOf('}');
    if (start >= 0 && end > start) {
      try {
        return JSON.parse(raw.slice(start, end + 1)) as T;
      } catch {
        return null;
      }
    }
    return null;
  };

  const resolveIconStyle = (value: string | undefined): IconStyle => {
    const normalized = String(value || '').toLowerCase() as IconStyle;
    return STYLES.includes(normalized) ? normalized : 'minimalist';
  };

  const resolveIconMood = (value: string | undefined): IconMood => {
    const normalized = String(value || '').toLowerCase() as IconMood;
    return MOODS.includes(normalized) ? normalized : 'professional';
  };

  const addChip = (
    value: string,
    setValue: React.Dispatch<React.SetStateAction<string>>,
    list: string[],
    setList: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    const next = value.trim();
    if (!next) return;
    if (!list.includes(next)) {
      setList([...list, next]);
    }
    setValue('');
  };

  const removeChip = (chip: string, setList: React.Dispatch<React.SetStateAction<string[]>>) => {
    setList((previous) => previous.filter((item) => item !== chip));
  };

  const handleGenerateGraphic = async () => {
    if (!selectedApp || !user?.email) {
      setError('Select an app and sign in first.');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      if (styleMode === 'ai' && !aiStylePromptDraft.trim()) {
        throw new Error('Generate feature graphic style first (or enter style prompt) before generating.');
      }

      const dto: CreateFeatureGraphicDTO = {
        appId: selectedApp.appId,
        appName: selectedApp.name,
        appDescription: selectedApp.description || '',
        appCategory: selectedApp.category,
        appPlatform: selectedApp.platform,
        appStoreUrl: selectedApp.storeUrl,
        androidStoreUrl: selectedApp.androidStoreUrl,
        iosStoreUrl: selectedApp.iosStoreUrl,
        researchContext: selectedApp.researchContext,
        style: styleMode === 'ai' ? aiSuggestedStyle : style,
        mood: styleMode === 'ai' ? aiSuggestedMood : mood,
        styleMode,
        aiStylePrompt: styleMode === 'ai' ? aiStylePromptDraft.trim() : undefined,
        aiSuggestedStyle,
        aiSuggestedMood,
        colorScheme:
          styleMode === 'ai'
            ? {}
            : {
                ...(primaryColor.trim() ? { primary: primaryColor.trim() } : {}),
                ...(secondaryColor.trim() ? { secondary: secondaryColor.trim() } : {}),
              },
        keywords: styleMode === 'ai' ? [] : keywords,
        avoidElements: styleMode === 'ai' ? [] : avoidElements,
        model: imageModel,
        customPrompt: styleMode === 'ai' ? '' : customPrompt,
      };

      const created = await FeatureGraphicService.generateFeatureGraphic(user.email, dto);
      setGeneratedGraphics((prev) => [...created, ...prev]);
      setSuccess(
        styleMode === 'ai'
          ? `Generated ${created.length} base feature graphic image(s) using AI style direction.`
          : `Generated ${created.length} base feature graphic image(s).`
      );
    } catch (generationError: any) {
      console.error(generationError);
      setError(generationError?.message || 'Failed to generate feature graphic.');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateAIStyle = async () => {
    if (!selectedApp || !user?.email) {
      setError('Select an app and sign in first.');
      return;
    }

    try {
      setGeneratingAIStyle(true);
      setError(null);
      setSuccess(null);

      const stylePrompt = [
        'You are an expert app marketing designer.',
        'Generate a concise visual style direction for an Android feature graphic prompt.',
        '',
        `App name: ${selectedApp.name}`,
        `Category: ${selectedApp.category || 'general'}`,
        `Platform target: android`,
        `Description: ${selectedApp.description || 'N/A'}`,
        `Research context: ${selectedApp.researchContext?.whatItDoes || selectedApp.researchContext?.summary || 'N/A'}`,
        '',
        'Return JSON only with:',
        '{ "style": "flat|gradient|3d|minimalist|illustrative|geometric", "mood": "professional|playful|elegant|bold|calm", "stylePrompt": "detailed feature-graphic art direction in 2-4 sentences" }',
      ].join('\n');

      const styleResult = await AIService.generateJSON<{
        style?: string;
        mood?: string;
        stylePrompt?: string;
      }>(stylePrompt, {
        provider: 'openai',
        model: 'gpt-4.1',
        structuredOutputName: 'feature_graphic_style_brief',
        structuredOutputSchema: {
          type: 'object',
          properties: {
            style: { type: 'string' },
            mood: { type: 'string' },
            stylePrompt: { type: 'string' },
          },
          required: ['style', 'mood', 'stylePrompt'],
        },
        temperature: 0.6,
        maxTokens: 500,
      });

      let nextStyle = resolveIconStyle(styleResult.style);
      let nextMood = resolveIconMood(styleResult.mood);
      let nextPrompt = String(styleResult.stylePrompt || '').trim();

      if (!nextPrompt) {
        const fallback = await AIService.generateText(stylePrompt, {
          provider: 'openai',
          model: 'gpt-4.1',
          temperature: 0.6,
          maxTokens: 500,
        });
        const parsed = parseJson<{ style?: string; mood?: string; stylePrompt?: string }>(
          fallback.text
        );
        nextStyle = resolveIconStyle(parsed?.style || nextStyle);
        nextMood = resolveIconMood(parsed?.mood || nextMood);
        nextPrompt = String(parsed?.stylePrompt || '').trim();
      }

      if (!nextPrompt) {
        nextPrompt =
          'Create a premium, conversion-focused Android feature graphic with a clean focal point, rich depth, and strong visual hierarchy.';
      }

      setAiSuggestedStyle(nextStyle);
      setAiSuggestedMood(nextMood);
      setAiStylePromptDraft(nextPrompt);
      setSuccess('AI feature graphic style generated. You can edit it before generating.');
    } catch (styleError: any) {
      console.error(styleError);
      setError(styleError?.message || 'Failed to generate AI feature graphic style.');
    } finally {
      setGeneratingAIStyle(false);
    }
  };

  const openCropDialog = (graphic: GeneratedFeatureGraphic) => {
    setSelectedGraphic(graphic);
    setCropSettings({ offsetX: 0, offsetY: 0, zoom: 1 });
    setFinalAsset(null);
    setCropDialogOpen(true);
  };

  const handleCreateFinalAsset = async () => {
    if (!selectedGraphic || !user?.email) return;
    setProcessing(true);
    setError(null);
    try {
      const asset = await FeatureGraphicService.createAndroidFeatureGraphicAsset(
        selectedGraphic,
        cropSettings
      );
      await FeatureGraphicService.saveGeneratedFeatureGraphicAsset(
        selectedGraphic.featureGraphicId,
        user.email,
        asset
      );
      setFinalAsset(asset);
      setSuccess('Android feature graphic generated (1024x500).');
    } catch (processingError: any) {
      console.error(processingError);
      setError(processingError?.message || 'Failed to generate Android feature graphic.');
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteGraphic = async (graphic: GeneratedFeatureGraphic) => {
    if (!user?.email) return;
    if (!window.confirm('Delete this feature graphic record?')) return;
    try {
      await FeatureGraphicService.deleteFeatureGraphic(graphic.featureGraphicId, user.email);
      setGeneratedGraphics((prev) =>
        prev.filter((item) => item.featureGraphicId !== graphic.featureGraphicId)
      );
    } catch (deleteError) {
      console.error(deleteError);
      setError('Failed to delete feature graphic.');
    }
  };

  const openPublishDialog = (graphic: GeneratedFeatureGraphic) => {
    setPublishTargetGraphic(graphic);
    setPublishDialogOpen(true);
    setPublishConfirmed(false);
    setFeatureGraphicValidationResult(null);
    setFeatureGraphicPublishResult(null);
    setPublishProgress(0);
    setPublishProgressLabel('');
    setPublishView('form');
    setExpandedPublishValidationSections({
      comparison: true,
      issues: true,
    });
    setError(null);
    setSuccess(null);
  };

  const closePublishDialog = () => {
    if (publishingGooglePlayFeatureGraphic) return;
    setPublishDialogOpen(false);
    setPublishTargetGraphic(null);
    setPublishConfirmed(false);
    setFeatureGraphicValidationResult(null);
    setFeatureGraphicPublishResult(null);
    setPublishProgress(0);
    setPublishProgressLabel('');
    setPublishView('form');
  };

  const handleValidateGooglePlayFeatureGraphic = async () => {
    if (!selectedApp || !publishTargetGraphic) {
      setError('Select a feature graphic before validating.');
      return;
    }

    try {
      setValidatingGooglePlayFeatureGraphic(true);
      setError(null);
      setFeatureGraphicPublishResult(null);
      const response = await FeatureGraphicService.validateGooglePlayFeatureGraphic({
        appId: selectedApp.appId,
        featureGraphicId: publishTargetGraphic.featureGraphicId,
      });
      setFeatureGraphicValidationResult(response);
      if (response.success) {
        setSuccess(response.message || 'Google Play feature graphic validation completed.');
      } else {
        setError(response.message || 'Feature graphic validation found issues.');
      }
    } catch (validationError: any) {
      console.error(validationError);
      const responseData = validationError?.responseData as GooglePlayFeatureGraphicValidationResponse;
      if (responseData?.packageName) {
        setFeatureGraphicValidationResult(responseData);
      }
      setError(
        responseData?.message ||
          validationError?.message ||
          'Failed to validate Google Play feature graphic.'
      );
    } finally {
      setValidatingGooglePlayFeatureGraphic(false);
    }
  };

  const handlePublishGooglePlayFeatureGraphic = async () => {
    if (!selectedApp || !publishTargetGraphic) {
      setError('Select a feature graphic before publishing.');
      return;
    }

    let progressTimer: number | null = null;
    let stageTimer: number | null = null;

    try {
      setPublishingGooglePlayFeatureGraphic(true);
      setPublishView('progress');
      setPublishProgress(18);
      setPublishProgressLabel('Preparing feature graphic for Google Play...');
      setError(null);
      setSuccess(null);

      progressTimer = window.setInterval(() => {
        setPublishProgress((prev) => (prev >= 92 ? prev : prev + 6));
      }, 350);

      stageTimer = window.setTimeout(() => {
        setPublishProgressLabel('Uploading feature graphic and committing Play edit...');
      }, 800);

      const response = await FeatureGraphicService.publishGooglePlayFeatureGraphic({
        appId: selectedApp.appId,
        featureGraphicId: publishTargetGraphic.featureGraphicId,
      });

      setPublishProgress(100);
      setPublishProgressLabel('Publish complete.');
      setFeatureGraphicPublishResult(response);
      setPublishView('success');
      setSuccess(response.message || 'Feature graphic published to Google Play.');
    } catch (publishError: any) {
      console.error(publishError);
      setPublishView('form');
      const responseData = publishError?.responseData as GooglePlayFeatureGraphicValidationResponse;
      if (responseData?.packageName) {
        setFeatureGraphicValidationResult(responseData);
      }
      setError(publishError?.message || 'Failed to publish feature graphic to Google Play.');
    } finally {
      if (progressTimer !== null) {
        window.clearInterval(progressTimer);
      }
      if (stageTimer !== null) {
        window.clearTimeout(stageTimer);
      }
      setPublishingGooglePlayFeatureGraphic(false);
    }
  };

  const openExpandedFeatureGraphicPreview = (title: string, url: string) => {
    const normalizedUrl = String(url || '').trim();
    if (!normalizedUrl) return;
    setExpandedFeatureGraphicPreview({
      open: true,
      title,
      url: normalizedUrl,
    });
  };

  const closeExpandedFeatureGraphicPreview = () => {
    setExpandedFeatureGraphicPreview({
      open: false,
      title: '',
      url: '',
    });
  };

  const handleHorizontalPositionChange = (value: number) => {
    setCropSettings((previous) => {
      const nextOffsetX = Number(value);
      const shouldAutoZoom = Math.abs(nextOffsetX) > 0 && previous.zoom <= 1;
      return {
        ...previous,
        offsetX: nextOffsetX,
        zoom: shouldAutoZoom ? 1.25 : previous.zoom,
      };
    });
  };

  const handleCropPreviewPointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    cropDragStateRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startOffsetX: cropSettings.offsetX,
      startOffsetY: cropSettings.offsetY,
      active: true,
    };
    setIsDraggingCropPreview(true);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handleCropPreviewPointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const dragState = cropDragStateRef.current;
    const metrics = cropPreviewMetricsRef.current;
    if (!dragState.active || dragState.pointerId !== event.pointerId || !metrics) {
      return;
    }

    const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);
    const dx = event.clientX - dragState.startX;
    const dy = event.clientY - dragState.startY;
    const deltaOffsetX = metrics.maxShiftX > 0 ? (-(dx / metrics.maxShiftX) * 100) : 0;
    const deltaOffsetY = metrics.maxShiftY > 0 ? (-(dy / metrics.maxShiftY) * 100) : 0;

    setCropSettings((previous) => ({
      ...previous,
      offsetX: clamp(dragState.startOffsetX + deltaOffsetX, -100, 100),
      offsetY: clamp(dragState.startOffsetY + deltaOffsetY, -100, 100),
    }));
  };

  const handleCropPreviewPointerEnd = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const dragState = cropDragStateRef.current;
    if (dragState.active && dragState.pointerId === event.pointerId) {
      cropDragStateRef.current = {
        pointerId: null,
        startX: 0,
        startY: 0,
        startOffsetX: 0,
        startOffsetY: 0,
        active: false,
      };
      setIsDraggingCropPreview(false);
      try {
        event.currentTarget.releasePointerCapture(event.pointerId);
      } catch {
        // no-op
      }
    }
  };

  useEffect(() => {
    if (!cropDialogOpen || !selectedGraphic?.baseImageUrl) {
      return;
    }

    const image = new Image();
    image.onload = () => setPreviewImage(image);
    image.onerror = () => setPreviewImage(null);
    image.src = selectedGraphic.baseImageUrl;
  }, [cropDialogOpen, selectedGraphic?.baseImageUrl]);

  useEffect(() => {
    if (!cropDialogOpen || !previewImage || !cropPreviewCanvasRef.current) {
      return;
    }

    const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);
    const outputWidth = 1024;
    const outputHeight = 500;
    const zoom = clamp(Number(cropSettings.zoom) || 1, 1, 3);
    const offsetX = clamp(Number(cropSettings.offsetX) || 0, -100, 100);
    const offsetY = clamp(Number(cropSettings.offsetY) || 0, -100, 100);

    const sourceWidth = previewImage.naturalWidth || outputWidth;
    const sourceHeight = previewImage.naturalHeight || outputHeight;
    const baseScale = Math.max(outputWidth / sourceWidth, outputHeight / sourceHeight);
    const drawScale = baseScale * zoom;
    const drawWidth = Math.round(sourceWidth * drawScale);
    const drawHeight = Math.round(sourceHeight * drawScale);
    const maxShiftX = Math.max(0, (drawWidth - outputWidth) / 2);
    const maxShiftY = Math.max(0, (drawHeight - outputHeight) / 2);
    cropPreviewMetricsRef.current = { maxShiftX, maxShiftY };
    const normalizedOffsetX = offsetX / 100;
    const normalizedOffsetY = offsetY / 100;
    const left = Math.round((drawWidth - outputWidth) / 2 + normalizedOffsetX * maxShiftX);
    const top = Math.round((drawHeight - outputHeight) / 2 + normalizedOffsetY * maxShiftY);
    const maxLeft = Math.max(0, drawWidth - outputWidth);
    const maxTop = Math.max(0, drawHeight - outputHeight);
    const safeLeft = clamp(left, 0, maxLeft);
    const safeTop = clamp(top, 0, maxTop);

    const offscreen = document.createElement('canvas');
    offscreen.width = drawWidth;
    offscreen.height = drawHeight;
    const offscreenCtx = offscreen.getContext('2d');
    if (!offscreenCtx) return;
    offscreenCtx.imageSmoothingEnabled = true;
    offscreenCtx.imageSmoothingQuality = 'high';
    offscreenCtx.drawImage(previewImage, 0, 0, drawWidth, drawHeight);

    const previewCanvas = cropPreviewCanvasRef.current;
    if (!previewCanvas) return;
    previewCanvas.width = outputWidth;
    previewCanvas.height = outputHeight;
    const previewCtx = previewCanvas.getContext('2d');
    if (!previewCtx) return;
    previewCtx.clearRect(0, 0, outputWidth, outputHeight);
    previewCtx.imageSmoothingEnabled = true;
    previewCtx.imageSmoothingQuality = 'high';
    previewCtx.drawImage(
      offscreen,
      safeLeft,
      safeTop,
      outputWidth,
      outputHeight,
      0,
      0,
      outputWidth,
      outputHeight
    );
  }, [cropDialogOpen, previewImage, cropSettings]);

  const publishBlockers: string[] = [];
  const selectedAppPackageName = String(selectedApp?.googlePlayPackageName || '').trim();
  if (!selectedApp) {
    publishBlockers.push('Select a registered app first.');
  } else if (!selectedAppPackageName) {
    publishBlockers.push(
      'Add a Google Play package name in App Registration before publishing a feature graphic.'
    );
  }
  if (!publishTargetGraphic) {
    publishBlockers.push('Select a feature graphic to publish.');
  }

  const hasValidationErrors = Boolean(
    featureGraphicValidationResult?.validationIssues?.some((issue) => issue.severity === 'error')
  );
  const canPublishFeatureGraphic =
    publishBlockers.length === 0 &&
    publishConfirmed &&
    Boolean(featureGraphicValidationResult) &&
    !hasValidationErrors &&
    !publishingGooglePlayFeatureGraphic;

  return (
    <Container maxWidth={false} sx={{ py: 4 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
        Android Feature Graphic Creator
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Generate Play Store feature-graphic concepts with app context, then crop to final Android spec (1024x500).
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Select a registered app</InputLabel>
              <Select
                value={selectedApp?.appId || ''}
                label="Select a registered app"
                onChange={(event: SelectChangeEvent) => {
                  const app = apps.find((candidate) => candidate.appId === event.target.value) || null;
                  setSelectedApp(app);
                }}
              >
                <MenuItem value="">
                  <em>Select a registered app</em>
                </MenuItem>
                {apps.map((app) => (
                  <MenuItem key={app.appId} value={app.appId}>
                    {app.name} ({app.platform})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {selectedApp && (
              <Box sx={{ mt: 2 }}>
                <Chip label={`Category: ${selectedApp.category}`} size="small" sx={{ mr: 1 }} />
                <Chip label="Target: Android only" size="small" color="primary" />
              </Box>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Generation Inputs
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Style Mode</InputLabel>
                  <Select
                    value={styleMode}
                    label="Style Mode"
                    onChange={(e) => setStyleMode(e.target.value as 'manual' | 'ai')}
                  >
                    {STYLE_MODES.map((mode) => (
                      <MenuItem key={mode.value} value={mode.value}>
                        {mode.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {styleMode === 'ai' && (
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={handleGenerateAIStyle}
                    disabled={generatingAIStyle || !selectedApp || loading}
                    startIcon={
                      generatingAIStyle ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />
                    }
                  >
                    {generatingAIStyle ? 'Generating AI Style...' : 'Generate Feature Graphic Style'}
                  </Button>
                </Grid>
              )}

              {styleMode === 'ai' && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={4}
                    label="AI Style Prompt (Editable)"
                    placeholder="Generate AI style first, then edit as needed."
                    value={aiStylePromptDraft}
                    onChange={(e) => setAiStylePromptDraft(e.target.value)}
                    helperText={`Detected style: ${aiSuggestedStyle} | mood: ${aiSuggestedMood}`}
                  />
                </Grid>
              )}

              {styleMode === 'manual' && (
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Style</InputLabel>
                    <Select value={style} label="Style" onChange={(e) => setStyle(e.target.value as IconStyle)}>
                      {STYLES.map((value) => (
                        <MenuItem key={value} value={value}>
                          {value}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              {styleMode === 'manual' && (
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Mood</InputLabel>
                    <Select value={mood} label="Mood" onChange={(e) => setMood(e.target.value as IconMood)}>
                      {MOODS.map((value) => (
                        <MenuItem key={value} value={value}>
                          {value}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Image Model</InputLabel>
                  <Select value={imageModel} label="Image Model" onChange={(e) => setImageModel(e.target.value)}>
                    {IMAGE_MODELS.map((model) => (
                      <MenuItem key={model.value} value={model.value}>
                        {model.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {styleMode === 'manual' && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Primary Color
                  </Typography>
                  <TextField
                    fullWidth
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    placeholder="#1976D2 (optional)"
                  />
                </Grid>
              )}
              {styleMode === 'manual' && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Secondary Color
                  </Typography>
                  <TextField
                    fullWidth
                    value={secondaryColor}
                    onChange={(e) => setSecondaryColor(e.target.value)}
                    placeholder="#DC004E (optional)"
                  />
                </Grid>
              )}
              {styleMode === 'manual' && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    label="Extra Prompt Direction (Optional)"
                    value={customPrompt}
                    onChange={(e) => setCustomPrompt(e.target.value)}
                  />
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Design Tokens
            </Typography>
            {styleMode === 'manual' ? (
              <>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">Keywords</Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <TextField fullWidth value={keywordsInput} onChange={(e) => setKeywordsInput(e.target.value)} />
                    <Button onClick={() => addChip(keywordsInput, setKeywordsInput, keywords, setKeywords)}>
                      Add
                    </Button>
                  </Box>
                  <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {keywords.map((value) => (
                      <Chip key={value} label={value} onDelete={() => removeChip(value, setKeywords)} />
                    ))}
                  </Box>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">Avoid Elements</Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <TextField fullWidth value={avoidInput} onChange={(e) => setAvoidInput(e.target.value)} />
                    <Button onClick={() => addChip(avoidInput, setAvoidInput, avoidElements, setAvoidElements)}>
                      Add
                    </Button>
                  </Box>
                  <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {avoidElements.map((value) => (
                      <Chip key={value} label={value} onDelete={() => removeChip(value, setAvoidElements)} />
                    ))}
                  </Box>
                </Box>
              </>
            ) : (
              <Alert severity="info" sx={{ mb: 2 }}>
                AI Style Mode is active. Manual style/mood/tokens are ignored, and generation uses the AI style prompt.
              </Alert>
            )}

            <Button
              variant="contained"
              fullWidth
              size="large"
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
              onClick={handleGenerateGraphic}
              disabled={loading || !selectedApp}
            >
              {loading ? 'Generating...' : 'Generate Base Graphic'}
            </Button>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              App Feature Graphic Assets ({generatedGraphics.length})
            </Typography>
            {generatedGraphics.length === 0 ? (
              <Alert severity="info">No graphics generated yet for this app.</Alert>
            ) : (
              <Grid container spacing={2}>
                {generatedGraphics.map((graphic) => (
                  <Grid item xs={12} sm={6} md={4} key={graphic.featureGraphicId}>
                    <Card>
                      <Box sx={{ height: 220, bgcolor: '#f2f4f7', overflow: 'hidden' }}>
                        <img
                          src={graphic.baseImageUrl}
                          alt="Feature graphic concept"
                          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        />
                      </Box>
                      <CardContent>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          Model: {graphic.metadata.aiModel}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {graphic.metadata.generatedAt.toLocaleString()}
                        </Typography>
                      </CardContent>
                      <CardActions>
                        <Button startIcon={<ContentCutIcon />} onClick={() => openCropDialog(graphic)}>
                          Finalize
                        </Button>
                        <Button
                          color="primary"
                          startIcon={<CloudUploadOutlinedIcon />}
                          onClick={() => openPublishDialog(graphic)}
                        >
                          Publish
                        </Button>
                        <Button color="error" startIcon={<DeleteIcon />} onClick={() => handleDeleteGraphic(graphic)}>
                          Delete
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={cropDialogOpen} onClose={() => setCropDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Finalize Android Feature Graphic (1024x500)</DialogTitle>
        <DialogContent>
          {selectedGraphic && (
            <>
              <Box
                sx={{
                  border: '1px solid #d0d5dd',
                  borderRadius: 2,
                  overflow: 'hidden',
                  width: '100%',
                  maxWidth: 760,
                  margin: '0 auto',
                  aspectRatio: '1024 / 500',
                  bgcolor: '#eceff3',
                }}
              >
                <canvas
                  ref={cropPreviewCanvasRef}
                  aria-label="Feature graphic crop preview"
                  onPointerDown={handleCropPreviewPointerDown}
                  onPointerMove={handleCropPreviewPointerMove}
                  onPointerUp={handleCropPreviewPointerEnd}
                  onPointerCancel={handleCropPreviewPointerEnd}
                  onPointerLeave={handleCropPreviewPointerEnd}
                  style={{
                    width: '100%',
                    height: '100%',
                    display: 'block',
                    cursor: isDraggingCropPreview ? 'grabbing' : 'grab',
                    touchAction: 'none',
                  }}
                />
              </Box>

              <Box sx={{ mt: 3 }}>
                <Typography gutterBottom>Zoom</Typography>
                <Slider
                  min={1}
                  max={3}
                  step={0.01}
                  value={cropSettings.zoom}
                  onChange={(_, value) =>
                    setCropSettings((previous) => ({ ...previous, zoom: Number(value) }))
                  }
                />
                <Typography gutterBottom>Horizontal Position</Typography>
                <Slider
                  min={-100}
                  max={100}
                  step={1}
                  value={cropSettings.offsetX}
                  onChange={(_, value) => handleHorizontalPositionChange(Number(value))}
                />
                <Typography gutterBottom>Vertical Position</Typography>
                <Slider
                  min={-100}
                  max={100}
                  step={1}
                  value={cropSettings.offsetY}
                  onChange={(_, value) =>
                    setCropSettings((previous) => ({ ...previous, offsetY: Number(value) }))
                  }
                />
              </Box>

              {finalAsset && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Final Asset Preview ({finalAsset.width}x{finalAsset.height})
                  </Typography>
                  <Box sx={{ borderRadius: 1, overflow: 'hidden', border: '1px solid #d0d5dd' }}>
                    <img
                      src={finalAsset.dataUrl}
                      alt="Final feature graphic"
                      style={{ width: '100%', height: 'auto', display: 'block' }}
                    />
                  </Box>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCropDialogOpen(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={handleCreateFinalAsset}
            startIcon={processing ? <CircularProgress size={16} color="inherit" /> : <ContentCutIcon />}
            disabled={processing || !selectedGraphic}
          >
            {processing ? 'Processing...' : 'Create 1024x500'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() =>
              finalAsset && selectedApp
                ? FeatureGraphicService.downloadFeatureGraphic(finalAsset, selectedApp.name)
                : undefined
            }
            disabled={!finalAsset || !selectedApp}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={publishDialogOpen}
        onClose={closePublishDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Publish Feature Graphic to Google Play</DialogTitle>
        <DialogContent dividers>
          {publishView === 'progress' && (
            <Box sx={{ py: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Publishing in progress
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {publishProgressLabel || 'Processing Google Play publish request...'}
              </Typography>
              <LinearProgress variant="determinate" value={publishProgress} />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {Math.round(publishProgress)}%
              </Typography>
            </Box>
          )}

          {publishView === 'success' && (
            <Box sx={{ py: 1 }}>
              <Alert severity="success" sx={{ mb: 2 }}>
                {featureGraphicPublishResult?.message ||
                  'Feature graphic published to Google Play.'}
              </Alert>
              <Typography variant="body2" color="text.secondary">
                The selected feature graphic is now live in your Google Play draft edit.
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={closePublishDialog}
                >
                  Done
                </Button>
              </Box>
            </Box>
          )}

          {publishView === 'form' && (
            <Box>
              <DialogContentText sx={{ mb: 2 }}>
                Validate and publish the selected feature graphic to your Google Play base locale.
              </DialogContentText>

              {publishBlockers.length > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {publishBlockers.join(' ')}
                </Alert>
              )}

              {publishTargetGraphic && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Feature graphic ID: {publishTargetGraphic.featureGraphicId}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Package: {selectedAppPackageName || 'Not set'}
                  </Typography>
                </Box>
              )}

              {featureGraphicValidationResult && (
                <Box sx={{ mt: 1 }}>
                  <Alert
                    severity={hasValidationErrors ? 'error' : 'info'}
                    sx={{ mb: 2 }}
                  >
                    {featureGraphicValidationResult.message ||
                      'Validation completed.'}
                  </Alert>

                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Proposed asset size:{' '}
                    {featureGraphicValidationResult.proposed?.width || 'unknown'}x
                    {featureGraphicValidationResult.proposed?.height || 'unknown'} (
                    {featureGraphicValidationResult.proposed?.format || 'png'})
                  </Typography>

                  <Accordion
                    expanded={expandedPublishValidationSections.comparison}
                    onChange={(_event, expanded) =>
                      setExpandedPublishValidationSections((previous) => ({
                        ...previous,
                        comparison: expanded,
                      }))
                    }
                    sx={{ mb: 1.5, bgcolor: 'transparent', border: '1px solid #d0d5dd', borderRadius: 1.5 }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        Current vs Proposed Feature Graphic
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2} sx={{ mb: 1 }}>
                        <Grid item xs={12} md={6}>
                          <Box
                            sx={{
                              border: '1px solid #d0d5dd',
                              borderRadius: 1.5,
                              overflow: 'hidden',
                              bgcolor: '#0f172a',
                            }}
                          >
                            <Box
                              sx={{
                                px: 1.5,
                                py: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                bgcolor: 'rgba(15,23,42,0.85)',
                              }}
                            >
                              <Typography variant="caption" sx={{ color: '#e2e8f0', fontWeight: 700 }}>
                                Current On Play ({featureGraphicValidationResult.current?.count || 0})
                              </Typography>
                              <Button
                                size="small"
                                sx={{ minWidth: 0, color: '#e2e8f0' }}
                                startIcon={<ZoomInIcon fontSize="small" />}
                                disabled={!featureGraphicValidationResult.current?.images?.[0]?.url}
                                onClick={() =>
                                  openExpandedFeatureGraphicPreview(
                                    'Current Feature Graphic On Play',
                                    featureGraphicValidationResult.current?.images?.[0]?.url || ''
                                  )
                                }
                              >
                                Expand
                              </Button>
                            </Box>
                            <Box sx={{ minHeight: 130, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              {featureGraphicValidationResult.current?.images?.[0]?.url ? (
                                <img
                                  src={featureGraphicValidationResult.current?.images?.[0]?.url}
                                  alt="Current Play feature graphic"
                                  style={{
                                    width: '100%',
                                    height: 'auto',
                                    display: 'block',
                                    cursor: 'zoom-in',
                                  }}
                                  onClick={() =>
                                    openExpandedFeatureGraphicPreview(
                                      'Current Feature Graphic On Play',
                                      featureGraphicValidationResult.current?.images?.[0]?.url || ''
                                    )
                                  }
                                />
                              ) : (
                                <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                                  No current feature graphic found on Play.
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <Box
                            sx={{
                              border: '1px solid #d0d5dd',
                              borderRadius: 1.5,
                              overflow: 'hidden',
                              bgcolor: '#0f172a',
                            }}
                          >
                            <Box
                              sx={{
                                px: 1.5,
                                py: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                bgcolor: 'rgba(15,23,42,0.85)',
                              }}
                            >
                              <Typography variant="caption" sx={{ color: '#e2e8f0', fontWeight: 700 }}>
                                Proposed For Publish
                              </Typography>
                              <Button
                                size="small"
                                sx={{ minWidth: 0, color: '#e2e8f0' }}
                                startIcon={<ZoomInIcon fontSize="small" />}
                                disabled={
                                  !(
                                    featureGraphicValidationResult.proposed?.playReadyFeatureGraphicUrl ||
                                    featureGraphicValidationResult.proposed?.featureGraphicUrl
                                  )
                                }
                                onClick={() =>
                                  openExpandedFeatureGraphicPreview(
                                    'Proposed Feature Graphic',
                                    featureGraphicValidationResult.proposed?.playReadyFeatureGraphicUrl ||
                                      featureGraphicValidationResult.proposed?.featureGraphicUrl ||
                                      ''
                                  )
                                }
                              >
                                Expand
                              </Button>
                            </Box>
                            <Box sx={{ minHeight: 130, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              {(featureGraphicValidationResult.proposed?.playReadyFeatureGraphicUrl ||
                                featureGraphicValidationResult.proposed?.featureGraphicUrl) ? (
                                <img
                                  src={
                                    featureGraphicValidationResult.proposed?.playReadyFeatureGraphicUrl ||
                                    featureGraphicValidationResult.proposed?.featureGraphicUrl
                                  }
                                  alt="Proposed feature graphic"
                                  style={{
                                    width: '100%',
                                    height: 'auto',
                                    display: 'block',
                                    cursor: 'zoom-in',
                                  }}
                                  onClick={() =>
                                    openExpandedFeatureGraphicPreview(
                                      'Proposed Feature Graphic',
                                      featureGraphicValidationResult.proposed?.playReadyFeatureGraphicUrl ||
                                        featureGraphicValidationResult.proposed?.featureGraphicUrl ||
                                        ''
                                    )
                                  }
                                />
                              ) : (
                                <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                                  No proposed feature graphic is available.
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>

                  {featureGraphicValidationResult.validationIssues?.length > 0 && (
                    <Accordion
                      expanded={expandedPublishValidationSections.issues}
                      onChange={(_event, expanded) =>
                        setExpandedPublishValidationSections((previous) => ({
                          ...previous,
                          issues: expanded,
                        }))
                      }
                      sx={{ mb: 1, bgcolor: 'transparent', border: '1px solid #d0d5dd', borderRadius: 1.5 }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          Validation Issues ({featureGraphicValidationResult.validationIssues.length})
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box sx={{ mb: 0.5 }}>
                          {featureGraphicValidationResult.validationIssues.map((issue) => (
                            <Alert
                              key={`${issue.field}-${issue.message}`}
                              severity={issue.severity === 'error' ? 'error' : 'warning'}
                              sx={{ mb: 1 }}
                            >
                              {issue.message}
                            </Alert>
                          ))}
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  )}
                </Box>
              )}

              <FormControlLabel
                sx={{ mt: 1 }}
                control={
                  <Checkbox
                    checked={publishConfirmed}
                    onChange={(event) => setPublishConfirmed(event.target.checked)}
                    disabled={publishBlockers.length > 0}
                  />
                }
                label="I confirm I want to publish this feature graphic to Google Play."
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closePublishDialog} disabled={publishingGooglePlayFeatureGraphic}>
            {publishView === 'success' ? 'Close' : 'Cancel'}
          </Button>
          {publishView === 'form' && (
            <>
              <Button
                onClick={handleValidateGooglePlayFeatureGraphic}
                disabled={validatingGooglePlayFeatureGraphic || publishBlockers.length > 0}
                startIcon={
                  validatingGooglePlayFeatureGraphic ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : undefined
                }
              >
                {validatingGooglePlayFeatureGraphic ? 'Validating...' : 'Validate'}
              </Button>
              <Button
                variant="contained"
                onClick={handlePublishGooglePlayFeatureGraphic}
                disabled={!canPublishFeatureGraphic}
                startIcon={
                  publishingGooglePlayFeatureGraphic ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <CloudUploadOutlinedIcon />
                  )
                }
              >
                {publishingGooglePlayFeatureGraphic ? 'Publishing...' : 'Publish to Google Play'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={expandedFeatureGraphicPreview.open}
        onClose={closeExpandedFeatureGraphicPreview}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pr: 1,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {expandedFeatureGraphicPreview.title || 'Feature Graphic Preview'}
          </Typography>
          <IconButton onClick={closeExpandedFeatureGraphicPreview}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ bgcolor: '#0f172a', p: { xs: 1.5, md: 2 } }}>
          {expandedFeatureGraphicPreview.url ? (
            <Box
              sx={{
                width: '100%',
                borderRadius: 1.5,
                overflow: 'hidden',
                border: '1px solid rgba(148,163,184,0.35)',
                bgcolor: '#0b1220',
              }}
            >
              <img
                src={expandedFeatureGraphicPreview.url}
                alt={expandedFeatureGraphicPreview.title || 'Feature graphic preview'}
                style={{ width: '100%', height: 'auto', display: 'block' }}
              />
            </Box>
          ) : (
            <Alert severity="info">No preview image available.</Alert>
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
}
